/**
 * Currency formatting utilities
 */

export type Currency = 'USD' | 'ZAR' | 'GBP';

export const currencySymbols: Record<Currency, string> = {
  USD: '$',
  ZAR: 'R',
  GBP: '£',
};

export const currencyNames: Record<Currency, string> = {
  USD: 'US Dollar',
  ZAR: 'Rand',
  GBP: 'Pound',
};

/**
 * Format a number as currency based on the selected currency
 * @param amount - The amount to format
 * @param currency - The currency code (USD, ZAR, GBP)
 * @returns Formatted currency string
 */
export const formatCurrency = (amount: number, currency: Currency = 'ZAR'): string => {
  const symbol = currencySymbols[currency];
  
  // Format number with appropriate locale
  const locale = currency === 'USD' ? 'en-US' : currency === 'ZAR' ? 'en-ZA' : 'en-GB';
  
  // For ZAR, we typically use R before the number (e.g., R1,234.56)
  // For GBP and USD, symbol is typically before (e.g., £1,234.56 or $1,234.56)
  if (currency === 'ZAR') {
    return `${symbol}${amount.toLocaleString(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  }
  
  return `${symbol}${amount.toLocaleString(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

/**
 * Format currency without decimal places (for display purposes)
 */
export const formatCurrencyNoDecimals = (amount: number, currency: Currency = 'ZAR'): string => {
  const symbol = currencySymbols[currency];
  const locale = currency === 'USD' ? 'en-US' : currency === 'ZAR' ? 'en-ZA' : 'en-GB';
  
  if (currency === 'ZAR') {
    return `${symbol}${amount.toLocaleString(locale, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
  }
  
  return `${symbol}${amount.toLocaleString(locale, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
};

